import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { FileText, RefreshCw, Download, Zap, AlertCircle, CheckCircle, Calendar, DollarSign, BarChart3, Building, TrendingUp, Archive } from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const ScheduleLNPreview = ({ calculationId }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState("scheduleL");
  const [scheduleL, setScheduleL] = useState(null);
  const [scheduleM, setScheduleM] = useState(null);
  const [scheduleN, setScheduleN] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);

  useEffect(() => { if (calculationId) loadSchedulesLN(); }, [calculationId]);

  const loadSchedulesLN = async () => {
    setLoading(true); setError(null);
    try {
      const response = await axios.get(`${API}/schedules-ln/${calculationId}`);
      const data = response.data;
      const schedules = data.schedules || {};
      setScheduleL(schedules.schedule_l);
      setScheduleM(schedules.schedule_m);
      setScheduleN(schedules.schedule_n);
      if (data.generated_timestamp) setLastGenerated(new Date(data.generated_timestamp).getTime());
    } catch (e) {
      if (e.response?.status !== 404) setError(e.response?.data?.detail || "Error loading Schedules L-N");
    } finally { setLoading(false); }
  };

  const handleGenerateAll = async () => {
    setGenerating(true); setError(null); setSuccess(null);
    try {
      const response = await axios.post(`${API}/generate-all-schedules-ln/${calculationId}`);
      if (response.data.status === 'success') {
        setSuccess("DCAA schedules generated successfully!");
        setTimeout(() => { loadSchedulesLN(); setSuccess(null); }, 1200);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error generating DCAA schedules");
    } finally { setGenerating(false); }
  };

  if (loading) return (<Card><CardContent className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div><p className="text-sm text-slate-600">Loading DCAA schedules...</p></CardContent></Card>);

  return (
    <div className="space-y-6">
      {error && (<Alert className="border-red-200 bg-red-50"><AlertCircle className="h-4 w-4" /><AlertDescription className="text-red-700">{error}</AlertDescription></Alert>)}
      {success && (<Alert className="border-green-200 bg-green-50"><CheckCircle className="h-4 w-4" /><AlertDescription className="text-green-700">{success}</AlertDescription></Alert>)}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2"><FileText className="h-5 w-5 text-blue-600" /><span>DCAA Allocation Summaries (Schedules L, M, N)</span></CardTitle>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={loadSchedulesLN} disabled={loading}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
              <Button onClick={handleGenerateAll} disabled={generating || !calculationId} className="bg-blue-600 hover:bg-blue-700"><Download className="h-4 w-4 mr-2" />{generating ? "Generating..." : "Generate All"}</Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* The existing large UI content remains unchanged below; it will render based on scheduleL, scheduleM, scheduleN presence */}
    </div>
  );
};

export default ScheduleLNPreview;